import { useEffect } from 'react'
import { create } from 'zustand'
import {
  buildPlan,
  daysUntil,
  ENGINE_VERSION
} from '@/lib/studyPlan/engine'
import { DEFAULT_STUDY_PLAN_CONFIG, STUDY_PLAN_EXAM_DATE } from '@/lib/studyPlan/config'
import type { PlanResult, PlanState, PlanBlock } from '@/lib/studyPlan/engine'
import {
  buildDayV2,
  buildWeekV2,
  weekStats,
  planMetaV2,
  ENGINE_VERSION_V2
} from '@/lib/studyPlan/v2/index'
import {
  claimQuizAttempt,
  coreDayComplete,
  denemeSpacingRows,
  planDayForDate,
  quizSpacingRow
} from '@/lib/studyPlan/v2/spacingSync'
import type { EngineV2Input } from '@/lib/studyPlan/v2/index'
import type { CheckInV2, PlanBlockV2, PlanDayV2, PlanStateV2 } from '@/lib/studyPlan/v2/types'
import {
  buildSubjectLessonIndex,
  resolveSubject,
  toStatusMap
} from '@/lib/studyPlan/resolve'
import type { SubjectLessonIndex, SubjectResolution } from '@/lib/studyPlan/resolve'
import { studyPlanService } from '@/services/studyPlanService'
import type {
  StoredPlanConfig,
  StudyPlanCompletion,
  DayLogEntry,
  DenemeResultEntry,
  TopicProgressRow,
  PlanResultV2,
  DayLogPutBody
} from '@/services/studyPlanService'
import { contentService } from '@/services/contentService'
import { progressService } from '@/services/progressService'
import { useAuthStore } from '@/store/authStore'
import { useLessonProgressStore } from '@/store/lessonProgressStore'
import type { Lesson, StudentProgress, Subject, Topic } from '@/types/index'

function toISO(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export function todayISO(): string {
  return toISO(new Date())
}

export function todayDayIdx(): number {
  return (new Date().getDay() + 6) % 7
}

export function mondayOf(d: Date): string {
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7))
  return toISO(monday)
}

export function weekDatesFrom(weekStart: string): string[] {
  const [y, m, d] = weekStart.split('-').map(Number)
  const monday = new Date(y, m - 1, d)
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday)
    day.setDate(monday.getDate() + i)
    return toISO(day)
  })
}

export function currentWeekDates(): string[] {
  return weekDatesFrom(mondayOf(new Date()))
}

export function rolloverWeekStart(saved: string | null): string {
  const current = mondayOf(new Date())
  if (!saved || saved < current) return current
  return saved
}

function toPlanState(config: StoredPlanConfig): PlanState | null {
  if (!config.area || !config.hours) return null
  return { area: config.area, goal: config.goal ?? '', hours: config.hours }
}

const HOURS_TO_MINUTES: Record<string, number> = {
  '1 saat': 60,
  '2 saat': 120,
  '3 saat': 180,
  '4+ saat': 240,
  '6 saat': 330,
  '8 saat': 480,
  '10 saat': 540
}

export function toPlanStateV2(config: StoredPlanConfig): PlanStateV2 | null {
  if (!config.area || typeof config.dailyMinutes !== 'number') return null
  const grade: PlanStateV2['grade'] = /mezun/i.test(config.grade) ? 'mezun' : 'lise'
  return {
    area: config.area,
    goal: config.goal ?? '',
    grade,
    dailyMinutes: config.dailyMinutes,
    kelime: config.kelime ?? (config.area === 'sozel' || config.area === 'dil'),
    dayStart: config.dayStart ?? null
  }
}

export function v2MinutesFromHours(hours: string | undefined): number {
  return HOURS_TO_MINUTES[hours ?? ''] ?? 180
}

export function doneByDateFrom(completions: StudyPlanCompletion[]): Record<string, Set<number>> {
  const map: Record<string, Set<number>> = {}
  completions.forEach((c) => {
    if (c.mins > 0) {
      if (!map[c.date]) map[c.date] = new Set()
      map[c.date].add(c.block_idx)
    }
  })
  return map
}

export function checkInsFrom(entries: DayLogEntry[]): Record<string, CheckInV2> {
  const map: Record<string, CheckInV2> = {}
  entries.forEach((e) => {
    map[e.date] = {
      date: e.date,
      energy: e.energy ?? 0,
      minDay: e.min_day === 1,
      checkedIn: e.checked_in === 1
    }
  })
  return map
}

export function blockTargetLesson(resolution: SubjectResolution | undefined, block: PlanBlock): number | null {
  if (!resolution) return null
  const sweep = block.type === 'quiz' || block.type === 'tekrar'
  return sweep ? resolution.gapLessonId ?? resolution.lessonId : resolution.lessonId
}

export function blockTargetDone(resolution: SubjectResolution | undefined, targetId: number | null): boolean {
  if (!resolution || targetId === null) return false
  return targetId === resolution.gapLessonId ? resolution.gapDone : resolution.lessonDone
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

interface ResolutionState {
  loading: boolean
  loaded: boolean
  bySubject: Record<string, SubjectResolution>
  index: SubjectLessonIndex | null
  subjects: Subject[]
  topics: Topic[]
  lessons: Lesson[]
  progress: StudentProgress[]
}

interface V2Content {
  subjects: Subject[]
  topics: Topic[]
  lessons: Lesson[]
  progress: StudentProgress[]
}

const initialResolution: ResolutionState = {
  loading: false,
  loaded: false,
  bySubject: {},
  index: null,
  subjects: [],
  topics: [],
  lessons: [],
  progress: []
}

function upsertDayLog(list: DayLogEntry[], entry: DayLogEntry): DayLogEntry[] {
  const idx = list.findIndex((e) => e.date === entry.date)
  if (idx === -1) return [entry, ...list]
  return list.map((e, i) => (i === idx ? entry : e))
}

function upsertDeneme(list: DenemeResultEntry[], entry: DenemeResultEntry): DenemeResultEntry[] {
  const idx = list.findIndex((e) => e.date === entry.date && e.kind === entry.kind)
  if (idx === -1) return [entry, ...list]
  return list.map((e, i) => (i === idx ? entry : e))
}

export interface LastCompleted {
  date: string
  dayIdx: number
  blockIdx: number
  mins: number
  subject: string
  kindLabel: string
}

export interface StudyPlanState {
  config: StoredPlanConfig | null
  plan: PlanResult | PlanResultV2 | null
  engineVersion: string | null
  status: 'active' | 'paused'
  completions: StudyPlanCompletion[]
  lastCompleted: LastCompleted | null
  weekStart: string
  regeneratedWeekStart: string | null
  resolution: ResolutionState
  dayLogs: DayLogEntry[]
  dayLogStreak: number | null
  denemeResults: DenemeResultEntry[]
  topicsProgress: TopicProgressRow[]
  loading: boolean
  saving: boolean
  error: string | null
  loaded: boolean

  fetchPlan: () => Promise<void>
  createPlan: (input: StoredPlanConfig) => Promise<boolean>
  regenerate: () => Promise<boolean>
  migrateToV2: () => Promise<void>
  setBlockCompletion: (date: string, dayIdx: number, blockIdx: number, mins: number, info?: { subject: string; kindLabel: string }) => Promise<void>
  clearLastCompleted: () => void
  changeStatus: (status: 'active' | 'paused') => Promise<void>
  ensureResolution: () => Promise<void>
  ensureV2Content: () => Promise<V2Content | null>
  fetchDayLogs: () => Promise<void>
  fetchDenemeResults: () => Promise<void>
  fetchTopicsProgress: () => Promise<void>
  checkIn: (date: string, energy: number, minDay: boolean, extras?: Partial<DayLogPutBody>) => Promise<void>
  logDeneme: (payload: { date: string; kind: 'tyt' | 'ayt' | 'brans'; nets: Record<string, number> }) => Promise<void>
  recordQuizResult: (attemptId: number, topicId: number | null | undefined, score: number) => Promise<void>
  markCoreDone: (date: string) => Promise<void>
  syncTopicsProgress: (rows: Partial<TopicProgressRow>[]) => Promise<void>
  reset: () => void
}

export const selectIsV2 = (s: StudyPlanState): boolean =>
  s.engineVersion === ENGINE_VERSION_V2 || (!!s.config && typeof s.config.dailyMinutes === 'number')

export const useStudyPlanStore = create<StudyPlanState>()((set, get) => ({
  config: null,
  plan: null,
  engineVersion: null,
  status: 'active',
  completions: [],
  lastCompleted: null,
  weekStart: mondayOf(new Date()),
  regeneratedWeekStart: null,
  resolution: initialResolution,
  dayLogs: [],
  dayLogStreak: null,
  denemeResults: [],
  topicsProgress: [],
  loading: false,
  saving: false,
  error: null,
  loaded: false,

  fetchPlan: async () => {
    if (get().loaded || get().loading) return
    set({ loading: true, error: null })
    try {
      const data = await studyPlanService.getPlan()
      if (data) {
        set({
          config: data.plan.config,
          plan: data.plan.plan_json,
          engineVersion: data.plan.engine_version,
          status: data.plan.status === 'paused' ? 'paused' : 'active',
          completions: data.completions,
          weekStart: rolloverWeekStart(get().weekStart),
          regeneratedWeekStart: data.plan.config?.regeneratedWeekStart ?? null,
          loaded: true,
          loading: false
        })
        get().fetchDayLogs()
        get().fetchDenemeResults()
        get().fetchTopicsProgress()
        if (!selectIsV2(get()) && data.plan.config?.area) {
          get().migrateToV2()
        }
      } else {
        set({ weekStart: rolloverWeekStart(get().weekStart), loaded: true, loading: false })
      }
    } catch (e) {
      set({ error: errorMessage(e, 'Çalışma planı yüklenemedi'), loading: false })
    }
  },

  createPlan: async (input) => {
    set({ saving: true, error: null })
    const prevStatus = get().status
    const weekStart = mondayOf(new Date())
    try {
      if (typeof input.dailyMinutes === 'number' && input.area) {
        const state2 = toPlanStateV2(input)
        if (!state2) {
          set({ saving: false })
          return false
        }
        const content = await get().ensureV2Content()
        if (!content) throw new Error('Ders içeriği yüklenemedi')
        if (get().topicsProgress.length === 0) {
          await get().fetchTopicsProgress()
        }
        const week = buildWeekV2({
          state: state2,
          subjects: content.subjects,
          topics: content.topics,
          lessons: content.lessons,
          progress: content.progress,
          topicsProgress: get().topicsProgress,
          weekStart
        })
        const meta = planMetaV2(state2)
        const ws = weekStats(week)
        const plan: PlanResultV2 = {
          engine: ENGINE_VERSION_V2,
          countdown: meta.countdown,
          focus: meta.areaLabel,
          phaseLabel: meta.phaseLabel,
          areaLabel: meta.areaLabel,
          week,
          stats: {
            totalMins: ws.totalMins,
            coreMins: ws.coreMins,
            video: ws.video,
            soru: ws.soru,
            deneme: ws.deneme,
            tekrar: ws.tekrar
          }
        }
        const cfg: StoredPlanConfig = { ...input, regeneratedWeekStart: weekStart }
        await studyPlanService.savePlan(cfg, plan, ENGINE_VERSION_V2)
        set({
          config: cfg,
          plan,
          engineVersion: ENGINE_VERSION_V2,
          status: prevStatus,
          weekStart,
          regeneratedWeekStart: weekStart,
          saving: false,
          loaded: true
        })
        return true
      }
      const state = toPlanState(input)
      if (!state) {
        set({ saving: false })
        return false
      }
      const result = buildPlan(state, DEFAULT_STUDY_PLAN_CONFIG)
      const cfg: StoredPlanConfig = { ...input, regeneratedWeekStart: weekStart }
      await studyPlanService.savePlan(cfg, result, ENGINE_VERSION)
      set({
        config: cfg,
        plan: result,
        engineVersion: ENGINE_VERSION,
        status: prevStatus,
        weekStart,
        regeneratedWeekStart: weekStart,
        saving: false,
        loaded: true
      })
      get().ensureResolution()
      return true
    } catch (e) {
      set({ error: errorMessage(e, 'Plan kaydedilemedi'), saving: false })
      return false
    }
  },

  regenerate: async () => {
    const { config } = get()
    if (!config) return false
    return get().createPlan(config)
  },

  migrateToV2: async () => {
    const { config } = get()
    if (!config || !config.area || get().engineVersion === ENGINE_VERSION_V2) return
    if (selectIsV2(get())) return
    const dailyMinutes = config.dailyMinutes ?? v2MinutesFromHours(config.hours)
    const kelime = config.kelime ?? (config.area === 'sozel' || config.area === 'dil')
    await get().createPlan({ ...config, dailyMinutes, kelime })
  },

  ensureResolution: async () => {
    const { resolution, plan } = get()
    if (resolution.loaded || resolution.loading || !plan) return
    const user = useAuthStore.getState().user
    if (!user) return
    set({ resolution: { ...resolution, loading: true } })
    try {
      const content = await loadV2Content()
      const index = buildSubjectLessonIndex(content.subjects, content.topics, content.lessons)
      const statusMap = toStatusMap(content.progress)
      const names = new Set<string>()
      const week = plan.week as { blocks: { subject?: string }[] }[]
      week.forEach((d) =>
        d.blocks.forEach((b) => {
          if (b.subject) names.add(b.subject)
        })
      )
      const bySubject: Record<string, SubjectResolution> = {}
      names.forEach((n) => {
        const r = resolveSubject(index, statusMap, n)
        if (r) bySubject[n] = r
      })
      set({
        resolution: {
          loading: false,
          loaded: true,
          bySubject,
          index,
          ...content
        }
      })
    } catch {
      set({ resolution: { ...get().resolution, loading: false, loaded: true, bySubject: {}, index: null } })
    }
  },

  ensureV2Content: async () => {
    const r = get().resolution
    if (r.subjects.length > 0 && r.lessons.length > 0) {
      return { subjects: r.subjects, topics: r.topics, lessons: r.lessons, progress: r.progress }
    }
    const user = useAuthStore.getState().user
    if (!user) return null
    try {
      const content = await loadV2Content()
      set({ resolution: { ...get().resolution, ...content } })
      return content
    } catch {
      return null
    }
  },

  fetchDayLogs: async () => {
    try {
      const res = await studyPlanService.getDayLogs()
      set({ dayLogs: res.entries })
    } catch {
      set({ dayLogs: [] })
    }
  },

  fetchDenemeResults: async () => {
    try {
      const res = await studyPlanService.getDenemeResults()
      set({ denemeResults: res.entries })
    } catch {
      set({ denemeResults: [] })
    }
  },

  fetchTopicsProgress: async () => {
    try {
      const res = await studyPlanService.getTopicsProgress()
      set({ topicsProgress: res.entries })
    } catch {
      set({ topicsProgress: [] })
    }
  },

  checkIn: async (date, energy, minDay, extras) => {
    const prev = get().dayLogs
    const optimistic: DayLogEntry = {
      date,
      template_id: null,
      energy,
      checked_in: 1,
      blocks_done: extras?.blocks_done ?? 0,
      blocks_planned: extras?.blocks_planned ?? 0,
      min_day: minDay ? 1 : 0,
      core_done: 0,
      note: null
    }
    set({ dayLogs: upsertDayLog(prev, optimistic), error: null })
    try {
      const res = await studyPlanService.putDayLog({
        date,
        energy,
        min_day: minDay ? 1 : 0,
        checked_in: 1,
        ...extras
      })
      set({
        dayLogs: upsertDayLog(get().dayLogs, res.entry),
        dayLogStreak: res.streak?.current_streak ?? get().dayLogStreak
      })
    } catch (e) {
      set({ dayLogs: prev, error: errorMessage(e, 'Günlük giriş kaydedilemedi') })
    }
  },

  logDeneme: async (payload) => {
    try {
      const res = await studyPlanService.postDenemeResult(payload)
      set({ denemeResults: upsertDeneme(get().denemeResults, res.entry), error: null })
      if (get().topicsProgress.length === 0) {
        await get().fetchTopicsProgress()
      }
      const rows = denemeSpacingRows(payload.nets, payload.date, get().topicsProgress)
      if (rows.length > 0) {
        await get().syncTopicsProgress(rows)
      }
    } catch (e) {
      set({ error: errorMessage(e, 'Deneme sonucu kaydedilemedi') })
    }
  },

  recordQuizResult: async (attemptId, topicId, score) => {
    if (!claimQuizAttempt(attemptId)) return
    if (typeof topicId !== 'number') return
    try {
      let topics = get().resolution.topics
      if (topics.length === 0) {
        topics = await contentService.getAllTopics()
      }
      const topic = topics.find((t) => t.id === topicId)
      if (!topic) return
      if (get().topicsProgress.length === 0) {
        await get().fetchTopicsProgress()
      }
      const row = quizSpacingRow(topic.name, score, get().topicsProgress)
      if (row) {
        await get().syncTopicsProgress([row])
      }
    } catch (e) {
      set({ error: errorMessage(e, 'Konu ilerlemesi kaydedilemedi') })
    }
  },

  markCoreDone: async (date) => {
    if (get().dayLogs.some((e) => e.date === date && e.core_done === 1)) return
    try {
      const res = await studyPlanService.putDayLog({ date, core_done: 1 })
      set({
        dayLogs: upsertDayLog(get().dayLogs, res.entry),
        dayLogStreak: res.streak?.current_streak ?? get().dayLogStreak
      })
    } catch (e) {
      set({ error: errorMessage(e, 'Çekirdek döngü kaydedilemedi') })
    }
  },

  syncTopicsProgress: async (rows) => {
    try {
      const res = await studyPlanService.postTopicsProgress(rows)
      set({ topicsProgress: res.entries, error: null })
    } catch (e) {
      set({ error: errorMessage(e, 'Konu ilerlemesi kaydedilemedi') })
    }
  },

  setBlockCompletion: async (date, dayIdx, blockIdx, mins, info) => {
    const prev = get().completions
    const matches = (c: StudyPlanCompletion) =>
      c.date === date && c.day_idx === dayIdx && c.block_idx === blockIdx
    const existing = prev.find(matches)
    const next = mins > 0
      ? existing
        ? prev.map((c) => (matches(c) ? { ...c, mins } : c))
        : [...prev, { date, day_idx: dayIdx, block_idx: blockIdx, mins }]
      : prev.filter((c) => !matches(c))
    set({ completions: next, error: null, lastCompleted: null })
    try {
      await studyPlanService.saveCompletions([{ date, day_idx: dayIdx, block_idx: blockIdx, mins }])
      if (mins > 0) {
        set({
          lastCompleted: {
            date,
            dayIdx,
            blockIdx,
            mins,
            subject: info?.subject ?? '',
            kindLabel: info?.kindLabel ?? 'Blok',
          },
        })
        const day = planDayForDate(get().plan, date)
        if (coreDayComplete(day, get().completions.filter((c) => c.date === date))) {
          await get().markCoreDone(date)
        }
      }
    } catch (e) {
      set({ completions: prev, error: errorMessage(e, 'Tamamlanma kaydedilemedi') })
    }
  },

  clearLastCompleted: () => set({ lastCompleted: null }),

  changeStatus: async (status) => {
    const prev = get().status
    set({ status, error: null })
    try {
      await studyPlanService.updateStatus(status)
    } catch (e) {
      set({ status: prev, error: errorMessage(e, 'Durum güncellenemedi') })
    }
  },

  reset: () =>
    set({
      config: null,
      plan: null,
      engineVersion: null,
      status: 'active',
      completions: [],
      lastCompleted: null,
      weekStart: mondayOf(new Date()),
      regeneratedWeekStart: null,
      resolution: initialResolution,
      dayLogs: [],
      dayLogStreak: null,
      denemeResults: [],
      topicsProgress: [],
      loading: false,
      saving: false,
      error: null,
      loaded: false
    })
}))

async function loadV2Content(): Promise<V2Content> {
  const user = useAuthStore.getState().user
  const [subjects, topics, lessons, progress] = await Promise.all([
    contentService.getAllSubjects(),
    contentService.getAllTopics(),
    contentService.getAllLessons(),
    user ? progressService.getAllUserProgress(user.id) : Promise.resolve([])
  ])
  return { subjects, topics, lessons, progress }
}

export const useIsBlockDone = (date: string, dayIdx: number, blockIdx: number) =>
  useStudyPlanStore(
    (s) =>
      s.completions.some(
        (c) => c.date === date && c.day_idx === dayIdx && c.block_idx === blockIdx && c.mins > 0
      )
  )

export interface WeekProgress {
  total: number
  done: number
  pct: number
  suggestUpdate: boolean
}

export const useWeekProgress = (): WeekProgress => {
  const plan = useStudyPlanStore((s) => s.plan)
  const completions = useStudyPlanStore((s) => s.completions)
  const weekStart = useStudyPlanStore((s) => s.weekStart)
  const regeneratedWeekStart = useStudyPlanStore((s) => s.regeneratedWeekStart)
  if (!plan) return { total: 0, done: 0, pct: 0, suggestUpdate: false }
  const dates = new Set(weekDatesFrom(weekStart))
  const week = plan.week as { blocks: unknown[] }[]
  const total = week.reduce((sum, d) => sum + d.blocks.length, 0)
  const done = Math.min(
    total,
    completions.filter((c) => dates.has(c.date) && c.mins > 0).length
  )
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return {
    total,
    done,
    pct,
    suggestUpdate: total > 0 && pct >= 70 && regeneratedWeekStart !== weekStart
  }
}

export interface TodayBlockView {
  block: PlanBlock
  blockIdx: number
  done: boolean
}

export interface TodayPlanSummary {
  hasPlan: boolean
  paused: boolean
  goal: string | null
  countdown: number
  blocks: TodayBlockView[]
  doneCount: number
}

export const useTodayPlan = (): TodayPlanSummary => {
  const plan = useStudyPlanStore((s) => s.plan)
  const config = useStudyPlanStore((s) => s.config)
  const status = useStudyPlanStore((s) => s.status)
  const completions = useStudyPlanStore((s) => s.completions)
  const resolution = useStudyPlanStore((s) => s.resolution)
  const progressCache = useLessonProgressStore((s) => s.progressCache)

  if (!plan || !config) {
    return { hasPlan: false, paused: false, goal: null, countdown: daysUntil(DEFAULT_STUDY_PLAN_CONFIG.examDate), blocks: [], doneCount: 0 }
  }
  const p = plan as PlanResult
  const dayIdx = todayDayIdx()
  const date = todayISO()
  const day = p.week[dayIdx]
  const views: TodayBlockView[] = (day ? day.blocks : []).map((block, blockIdx) => {
    const r = resolution.bySubject[block.subject]
    const targetId = blockTargetLesson(r, block)
    return {
      block,
      blockIdx,
      done:
        completions.some(
          (c) => c.date === date && c.day_idx === dayIdx && c.block_idx === blockIdx && c.mins > 0
        ) ||
        (targetId !== null && progressCache[targetId]?.status === 'completed') ||
        blockTargetDone(r, targetId)
    }
  })
  return {
    hasPlan: true,
    paused: status === 'paused',
    goal: config.goal || null,
    countdown: daysUntil(DEFAULT_STUDY_PLAN_CONFIG.examDate),
    blocks: views,
    doneCount: views.filter((v) => v.done).length
  }
}

export const useEngineV2Input = (): EngineV2Input | null => {
  const config = useStudyPlanStore((s) => s.config)
  const weekStart = useStudyPlanStore((s) => s.weekStart)
  const resolution = useStudyPlanStore((s) => s.resolution)
  const topicsProgress = useStudyPlanStore((s) => s.topicsProgress)
  const dayLogs = useStudyPlanStore((s) => s.dayLogs)
  const completions = useStudyPlanStore((s) => s.completions)
  const ensureResolution = useStudyPlanStore((s) => s.ensureResolution)
  useEffect(() => {
    ensureResolution()
  }, [ensureResolution])
  const state = config ? toPlanStateV2(config) : null
  if (!state || !resolution.loaded || resolution.subjects.length === 0) return null
  return {
    state,
    subjects: resolution.subjects,
    topics: resolution.topics,
    lessons: resolution.lessons,
    progress: resolution.progress,
    topicsProgress,
    weekStart,
    checkIns: checkInsFrom(dayLogs),
    doneByDate: doneByDateFrom(completions)
  }
}

export interface TodayPlanV2Summary {
  ready: boolean
  hasPlan: boolean
  paused: boolean
  goal: string | null
  countdown: number
  day: PlanDayV2 | null
  doneCount: number
  checkedIn: boolean
  minDay: boolean
  energy: number
}

export const useTodayPlanV2 = (): TodayPlanV2Summary => {
  const config = useStudyPlanStore((s) => s.config)
  const status = useStudyPlanStore((s) => s.status)
  const dayLogs = useStudyPlanStore((s) => s.dayLogs)
  const progressCache = useLessonProgressStore((s) => s.progressCache)
  const input = useEngineV2Input()
  const date = todayISO()
  const todayLog = dayLogs.find((e) => e.date === date)
  const base: TodayPlanV2Summary = {
    ready: false,
    hasPlan: !!config,
    paused: status === 'paused',
    goal: config?.goal || null,
    countdown: daysUntil(STUDY_PLAN_EXAM_DATE),
    day: null,
    doneCount: 0,
    checkedIn: todayLog?.checked_in === 1,
    minDay: todayLog?.min_day === 1,
    energy: todayLog?.energy ?? 0
  }
  if (!input) return base
  const day = buildDayV2(input, date)
  day.blocks.forEach((b) => {
    if (b.lessonId && progressCache[b.lessonId]?.status === 'completed') b.done = true
  })
  return {
    ...base,
    ready: true,
    day,
    doneCount: day.blocks.filter((b) => b.done).length
  }
}

export type { PlanBlockV2, PlanDayV2 }
